import { formatTimeLogDateTimeLabel } from "@jugl-web/domain-resources/task-time-tracking/utils";
import { ActiveTaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { InteractiveContainer } from "@jugl-web/ui-components";
import {
  CTAPopup,
  CTAPopupHandle,
} from "@jugl-web/ui-components/cross-platform/CTAPopup";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { formatSecondsToDuration } from "@jugl-web/utils/date-time/formatSecondsToDuration";
import { differenceInSeconds } from "date-fns";
import { FC, ReactNode, forwardRef } from "react";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";
import { ReactComponent as ChevronRightIcon } from "./assets/chevron-right.svg";
import { ReactComponent as ClockIcon } from "./assets/clock.svg";
import { ReactComponent as TaskIcon } from "./assets/task.svg";

interface ActiveTrackingStoppedDialogProps {
  isOpen: boolean;
  activeTimeLog: ActiveTaskTimeLogModel | undefined;
  reasonMessage?: string;
  onClose: () => void;
  onAfterClose?: () => void;
  onNavigateToTask?: (taskId: string, taskBoardId: string | null) => void;
}

export const ActiveTrackingStoppedDialog = forwardRef<
  CTAPopupHandle,
  ActiveTrackingStoppedDialogProps
>(
  (
    {
      isOpen,
      activeTimeLog,
      reasonMessage,
      onClose,
      onAfterClose,
      onNavigateToTask,
    },
    ref
  ) => {
    const { t } = useTranslations();
    const { isMobile } = useAppVariant();

    return (
      <CTAPopup
        ref={ref}
        isOpen={isOpen}
        hasCloseButton
        icon="⏰"
        title={t({
          id: "tasks-page.tracking-stopped-heading",
          defaultMessage: "Time tracking got stopped",
        })}
        content={
          activeTimeLog && (
            <PopupContent
              activeTimeLog={activeTimeLog}
              reasonMessage={reasonMessage}
              onNavigateToTask={onNavigateToTask}
              onClose={onClose}
            />
          )
        }
        buttons={
          isMobile
            ? [
                {
                  label: t({ id: "common.okay", defaultMessage: "Okay" }),
                  color: "primary",
                  onClick: onClose,
                },
              ]
            : []
        }
        onClose={onClose}
        onAfterClose={onAfterClose}
      />
    );
  }
);

interface PopupContentProps {
  activeTimeLog: ActiveTaskTimeLogModel;
  reasonMessage: ActiveTrackingStoppedDialogProps["reasonMessage"];
  onNavigateToTask: ActiveTrackingStoppedDialogProps["onNavigateToTask"];
  onClose: ActiveTrackingStoppedDialogProps["onClose"];
}

const PopupContent: FC<PopupContentProps> = ({
  activeTimeLog,
  reasonMessage,
  onNavigateToTask,
  onClose,
}) => {
  const { t } = useTranslations();

  return (
    <div className="flex flex-col gap-4">
      {reasonMessage && (
        <span className="text-dark-600 font-secondary text-sm">
          {reasonMessage}
        </span>
      )}
      <div className="font-secondary -mx-2 flex flex-col gap-2 pt-2 text-[15px]">
        <ListItem
          icon={<TaskIcon className="shrink-0" />}
          label={t({
            id: "tasks-page.item",
            defaultMessage: "Item",
          })}
          value={activeTimeLog.taskName}
          onClick={
            onNavigateToTask
              ? () => {
                  onNavigateToTask(activeTimeLog.taskId, activeTimeLog.boardId);
                  onClose();
                }
              : undefined
          }
        />
        <ListItem
          icon={<CalendarIcon className="shrink-0" />}
          label={t({
            id: "tasks-page.date",
            defaultMessage: "Date",
          })}
          value={formatTimeLogDateTimeLabel({
            startDate: activeTimeLog.startDate,
            endDate: activeTimeLog.endDate,
          })}
        />
        <ListItem
          icon={<ClockIcon className="shrink-0" />}
          label={t({
            id: "tasks-page.duration",
            defaultMessage: "Duration",
          })}
          value={formatSecondsToDuration(
            differenceInSeconds(
              activeTimeLog.endDate || new Date(),
              activeTimeLog.startDate
            )
          )}
        />
      </div>
    </div>
  );
};

interface ListItemProps {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  onClick?: () => void;
}

const ListItem: FC<ListItemProps> = ({ icon, label, value, onClick }) => {
  const isInteractive = !!onClick;

  return (
    <InteractiveContainer
      className={cx(
        "group flex h-10 items-center justify-between gap-2 px-2 transition-colors",
        isInteractive && "hover:bg-grey-100 rounded-lg"
      )}
      isDisabled={!isInteractive}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        {icon}
        <span className="text-dark-600">{label}:</span>
        <span className="text-dark-900 truncate font-medium">{value}</span>
      </div>
      {isInteractive && (
        <ChevronRightIcon className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
      )}
    </InteractiveContainer>
  );
};
